import { useState, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';
import imageCompression from 'browser-image-compression';
import { Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface ImageUploadProps {
    strainId: string;
    onUploadComplete: () => void;
}

export default function ImageUpload({ strainId, onUploadComplete }: ImageUploadProps) {
    const { user } = useAuth();
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFiles = async (files: FileList | null) => {
        if (!files || files.length === 0 || !user) return;

        setUploading(true);
        const file = files[0]; // Process one at a time for now

        try {
            // 1. Compress Image
            // 1. Compress Image

            const options = {
                maxSizeMB: 0.5, // Target 500KB max
                maxWidthOrHeight: 1200,
                useWebWorker: true,
                fileType: 'image/webp'
            };

            const compressedFile = await imageCompression(file, options);

            // 2. Upload to Storage
            const fileExt = 'webp';
            const fileName = `${strainId}/${Date.now()}.${fileExt}`;
            const { error: uploadError } = await supabase.storage
                .from('strain-images')
                .upload(fileName, compressedFile);

            if (uploadError) throw uploadError;

            // 3. Link in Database
            const { error: dbError } = await supabase
                .from('strain_images')
                .insert({
                    strain_id: strainId,
                    user_id: user.id,
                    storage_path: fileName,
                    is_primary: false // Logic to set primary can be added later
                });

            if (dbError) throw dbError;

            onUploadComplete();
        } catch (error) {
            console.error('Upload failed:', error);
            alert('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFiles(e.dataTransfer.files);
        }
    };

    return (
        <div className="w-full">
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => handleFiles(e.target.files)}
            />

            <div
                className={`relative group cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed transition-all duration-300 h-32 flex flex-col items-center justify-center gap-2 ${dragActive
                    ? 'border-accent-purple bg-accent-purple/10 scale-[1.02]'
                    : uploading
                        ? 'border-dark-border bg-dark-surface/50 opacity-50 pointer-events-none'
                        : 'border-dark-border/60 bg-dark-surface/20 hover:border-accent-purple/50 hover:bg-dark-surface/40'
                    }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                {uploading ? (
                    <>
                        <Loader2 className="animate-spin text-accent-purple" size={24} />
                        <p className="text-[10px] font-black uppercase tracking-widest text-dark-muted animate-pulse">Compressing & Encrypting...</p>
                    </>
                ) : (
                    <>
                        <div className="p-2 bg-dark-bg rounded-full border border-dark-border group-hover:scale-110 transition-transform">
                            {dragActive ? <Upload size={20} className="text-accent-purple" /> : <ImageIcon size={20} className="text-dark-muted group-hover:text-white" />}
                        </div>
                        <div className="text-center">
                            <p className="text-xs font-bold text-white group-hover:text-accent-purple transition-colors">
                                {dragActive ? 'Drop to Upload' : 'Upload Evidence'}
                            </p>
                            <p className="text-[9px] font-medium text-dark-muted/60 mt-0.5">
                                JPG, PNG, WEBP • Max 10MB
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
